"use client";

import { ArrowDownRight, ArrowRight, ArrowUpRight, Minus, X } from "lucide-react";
import type { KeyIndicatorEntry, KeyIndicatorTrend, KeyIndicatorsDetails } from "../../types";
import { RuntimeLink, useT } from "../../runtime/RendererRuntime";
import { OverlayDialog } from "../../components/overlays/OverlayDialog";
import {
	POLARITE_DU_CHANGE,
	TON_TEXTE,
	polariteIndicateur,
	tonTendance,
	type IndicatorPolarity,
} from "./trendSemantics";

/**
 * Couleur d'une variation : elle dit si le mouvement va dans le bon sens pour CET
 * indicateur, jamais s'il monte ou s'il descend (cf. `trendSemantics`). Un indicateur
 * dont le sens n'est pas connu reste gris.
 */
export function couleurVariation(trend: KeyIndicatorTrend | undefined, polarite: IndicatorPolarity): string {
	return TON_TEXTE[tonTendance(trend, polarite)];
}

/**
 * Flèche de tendance du bandeau et de la vue complète. Ce sont les MÊMES icônes que
 * les cartes « Marchés et indicateurs » : flèches obliques fines, trait plat quand rien
 * ne bouge. Le bandeau portait auparavant des flèches pleines, verticales et plus
 * lourdes ; deux dessins pour une même information se lisaient comme deux mesures
 * différentes.
 *
 * Définie ici plutôt que dans le bandeau parce que celui-ci importe la modale : garder
 * la dépendance dans un seul sens évite un cycle.
 */
export function TrendIcon({ trend, className }: { trend: KeyIndicatorTrend; className?: string }) {
	if (trend === "up") return <ArrowUpRight aria-hidden className={className} />;
	if (trend === "down") return <ArrowDownRight aria-hidden className={className} />;
	return <Minus aria-hidden className={className} />;
}

const TITLE_ID = "bcc-key-indicators-modal-title";

/** Une case de la grille : un libellé, une valeur, et le mouvement s'il est connu. */
interface Tuile {
	label: string;
	value: string;
	unit?: string;
	delta?: string;
	trend?: KeyIndicatorTrend;
	/** Seconde valeur du même sujet (ex. le taux maximum d'un Bon BCC). */
	hint?: string;
	/** Code drapeau (flag-icons) quand il aide à repérer la case : les devises. */
	flag?: string;
	/**
	 * Sens de lecture, quand le sujet le connaît mieux que le libellé. Une ligne du
	 * fragment change est un cours, même si son libellé ne le dit pas.
	 */
	polarite?: IndicatorPolarity;
}

function IndicatorTile({ tuile }: { tuile: Tuile }) {
	const trend = tuile.trend ?? "flat";
	const polarite = tuile.polarite ?? polariteIndicateur(tuile.label);
	return (
		<div className="rounded-md bg-white px-3 py-2.5 ring-1 ring-slate-200/80">
			<div className="flex items-center gap-2 text-[11px] font-semibold uppercase leading-4 tracking-wide text-slate-600">
				{/* Drapeau seulement là où il aide vraiment à repérer la case : les devises.
				    Un taux ou un indice n'a pas de pays. */}
				{tuile.flag ? (
					<span
						className={`fi fi-${tuile.flag} shrink-0 text-[13px]`}
						style={{ display: "inline-flex", boxShadow: "0px 1px 2px rgba(0,0,0,0.18)" }}
						aria-hidden="true"
					/>
				) : null}
				<span className="min-w-0">{tuile.label}</span>
			</div>
			<div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
				<span className="text-[18px] font-bold tabular-nums leading-tight text-[#005CA9]">
					{tuile.value}
					{tuile.unit ? <span className="ml-1 text-[13px] font-medium">{tuile.unit}</span> : null}
				</span>
				{tuile.delta ? (
					<span
						className={`inline-flex items-center gap-1 text-[12px] font-semibold tabular-nums ${couleurVariation(trend, polarite)}`}
					>
						<TrendIcon trend={trend} className="h-3 w-3" />
						{tuile.delta}
					</span>
				) : null}
			</div>
			{tuile.hint ? <div className="mt-0.5 text-[11px] tabular-nums text-slate-500">{tuile.hint}</div> : null}
		</div>
	);
}

/**
 * Dernière case de la grille : elle n'affiche pas un chiffre, elle emmène aux statistiques.
 * Rangée avec les autres plutôt qu'en pied de panneau, pour rester dans le geste de lecture.
 *
 * EXACTEMENT la même carte que les valeurs, filet gris compris : ni aplat plein, ni bordure
 * de couleur, qui l'un comme l'autre criaient au milieu de seize cases calmes. Ce qui la
 * désigne comme un geste, c'est la flèche ambre et le libellé — et la teinte qui n'apparaît
 * qu'au survol.
 *
 * Le clic REFERME le panneau : la navigation est interne, le bandeau vit dans la mise en page
 * commune, donc la vue serait restée ouverte par-dessus la page d'arrivée, qu'on venait
 * justement demander à voir.
 */
function StatisticsTile({ href, label, onNavigate }: { href: string; label: string; onNavigate: () => void }) {
	const tr = useT();
	return (
		<RuntimeLink
			href={href}
			onClick={onNavigate}
			className="group flex flex-col justify-between rounded-md bg-white px-3 py-2.5 ring-1 ring-slate-200/80 transition hover:bg-[#EAF6FF]"
		>
			<span className="text-[15px] font-bold leading-tight text-[#005CA9]">{label}</span>
			<span className="mt-1 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
				{tr("menuOverview")}
				<ArrowRight className="h-4 w-4 shrink-0 text-[#FFA401] transition group-hover:translate-x-0.5" aria-hidden="true" />
			</span>
		</RuntimeLink>
	);
}

/**
 * Dans la grille, chaque case est lue SEULE : « Hebdomadaire » ne dit pas de quoi il
 * s'agit, alors que la case voisine annonce « Inflation (annualisée) ». Chaque mesure
 * d'inflation porte donc son sujet, en restant courte : le sujet, puis la mesure entre
 * parenthèses. Sur la carte d'accueil, où le titre dit déjà « Inflation », les lignes
 * gardent leur libellé nu : cette reprise n'existe que dans la grille.
 */
const LIBELLES_INFLATION: Record<string, string> = {
	hebdomadaire: "Inflation (hebdomadaire)",
	"cumulé de l'année": "Inflation (cumul annuel)",
	"cumule de l'annee": "Inflation (cumul annuel)",
	"glissement annuel": "Inflation (glissement annuel)",
	annualisé: "Inflation (annualisée)",
	annualisée: "Inflation (annualisée)",
};

export function libelleInflation(brut: string): string {
	const propre = (brut ?? "").trim();
	if (!propre) return propre;
	const connu = LIBELLES_INFLATION[propre.toLowerCase()];
	if (connu) return connu;
	// Mesure inédite : même règle, sans liste à tenir à jour.
	return /inflation/i.test(propre) ? propre : `Inflation (${propre.toLowerCase()})`;
}

/** Une famille de la grille : les valeurs d'un même sujet se suivent. */
interface Famille {
	cle: string;
	/** Le sens de lecture du sujet, qui prime sur le libellé de chaque ligne. */
	polarite: IndicatorPolarity;
	tuiles: Tuile[];
}

/**
 * Toutes les valeurs disponibles, mises à plat et RANGÉES PAR SUJET.
 *
 * Le bandeau en porte six ; les trois fragments de marché en portent une dizaine d'autres.
 * On les rassemble en cases de même facture — pas de cartes, pas de tableaux, pas de
 * graphiques : la demande est une grille.
 *
 * L'ordre suit les sujets, jamais la provenance. Sans cela le yuan se retrouvait loin des
 * trois autres devises, simplement parce que le bandeau ne le porte pas : les six valeurs du
 * bandeau passaient d'abord, les fragments ensuite. Ici chaque famille (change, inflation,
 * taux, Bons BCC) est reconstituée, la valeur du bandeau en tête puisqu'elle porte la
 * variation du jour, puis ce que le fragment ajoute.
 *
 * Dédoublonnage À L'INTÉRIEUR d'une famille, sur la valeur : le taux directeur du bandeau et
 * « Prêt par adjudications » sont le même taux sous deux noms, on garde le nom courant.
 * Jamais entre familles : le Bon BCC à 84 jours vaut aussi 13,50 %, et il resterait invisible.
 * Une valeur du bandeau qu'aucune famille ne reconnaît (le coefficient de réserve) ferme la
 * marche — aucune règle par sujet, un futur indicateur inédit apparaîtra tout seul.
 */
export function construireTuiles(
	indicators: KeyIndicatorEntry[],
	details: KeyIndicatorsDetails | undefined,
): Tuile[] {
	const normaliser = (valeur: string): string => valeur.replace(/\s*%$/, "").trim();
	// Les drapeaux ne vivent que dans le fragment change ; le bandeau ne porte que des libellés.
	const drapeaux = new Map<string, string>();
	(details?.exchange?.rows ?? []).forEach((row) => {
		if (row.flag) drapeaux.set(`${row.code}/CDF`.toLowerCase(), row.flag);
	});

	// Ce que les fragments apportent, famille par famille, dans l'ordre de lecture.
	const familles: Famille[] = [
		{
			cle: "change",
			// Un seul point de réglage pour tout le site, cf. `POLARITE_DU_CHANGE`.
			polarite: POLARITE_DU_CHANGE,
			tuiles: (details?.exchange?.rows ?? []).map((row) => ({
				label: `${row.code}/CDF`,
				value: row.value,
				unit: "CDF",
				trend: row.trend,
			})),
		},
		{
			cle: "inflation",
			polarite: "baisse-favorable",
			tuiles: (details?.inflation?.rows ?? []).map((row) => ({
				label: libelleInflation(row.label),
				value: row.value,
				unit: "%",
			})),
		},
		{
			cle: "taux",
			// La Banque fixe ses taux : leur mouvement est une décision, pas une performance.
			polarite: "neutre",
			tuiles: (details?.interest?.directeur?.rows ?? []).map((row) => ({
				label: row.label,
				value: row.value,
				trend: row.trend,
			})),
		},
		{
			cle: "bons",
			polarite: "neutre",
			tuiles: (details?.interest?.bons?.rows ?? []).map((row) => ({
				label: `Bons BCC ${row.jours} j`,
				value: row.tmp,
				hint: row.tm ? `Tm ${row.tm}` : undefined,
			})),
		},
	];

	// À quelle famille appartient chaque valeur du bandeau ? À celle qui porte déjà le même
	// chiffre. Rien de plus : c'est la donnée qui range, pas une liste de libellés en dur.
	const familleDe = new Map<string, string>();
	familles.forEach((famille) =>
		famille.tuiles.forEach((tuile) => {
			const cle = normaliser(tuile.value);
			if (!familleDe.has(cle)) familleDe.set(cle, famille.cle);
		}),
	);

	const tuiles: Tuile[] = [];
	const vues = new Set<string>();
	// Le sens de lecture vient du SUJET, pas du libellé de la ligne : « Annualisé » ne dit pas
	// qu'il s'agit d'inflation, et « USD » ne dit pas qu'il s'agit d'un cours de change.
	const polariteDe = new Map(familles.map((famille) => [famille.cle, famille.polarite]));
	const ajouter = (tuile: Tuile, famille: string): void => {
		if (!tuile.value) return;
		const empreinte = `${famille}|${normaliser(tuile.value)}`;
		if (vues.has(empreinte)) return;
		vues.add(empreinte);
		tuiles.push({
			...tuile,
			flag: tuile.flag ?? drapeaux.get(tuile.label.toLowerCase()),
			// Hors famille connue (« autres »), c'est le libellé qui tranche, ou le gris.
			polarite: tuile.polarite ?? polariteDe.get(famille) ?? polariteIndicateur(tuile.label),
		});
	};

	familles.forEach((famille) => {
		// La valeur du bandeau d'abord : elle porte la variation du jour et le nom courant.
		indicators
			.filter((indicator) => familleDe.get(normaliser(indicator.value)) === famille.cle)
			.forEach((indicator) =>
				ajouter(
					{
						label: indicator.label,
						value: indicator.value,
						unit: indicator.unit,
						delta: indicator.delta,
						trend: indicator.trend,
					},
					famille.cle,
				),
			);
		famille.tuiles.forEach((tuile) => ajouter(tuile, famille.cle));
	});

	// Ce que le bandeau est seul à porter ferme la marche.
	indicators
		.filter((indicator) => !familleDe.has(normaliser(indicator.value)))
		.forEach((indicator) =>
			ajouter(
				{
					label: indicator.label,
					value: indicator.value,
					unit: indicator.unit,
					delta: indicator.delta,
					trend: indicator.trend,
				},
				"autres",
			),
		);

	return tuiles;
}

/**
 * Modale des indicateurs clés : la version immobile et complète du bandeau.
 *
 * Le bandeau ne montre qu'une valeur à la fois, au rythme de son défilement ; pour lire le
 * taux directeur il fallait l'attendre. Ici tout est posé d'un coup, en une grille de cases
 * identiques, et un bouton mène aux statistiques. Les cartes du tableau de bord d'accueil
 * ont été essayées : trop de matière pour un panneau qu'on ouvre en passant.
 *
 * Les données arrivent déjà chargées avec la page (cf. `overlayBelowHeaderBlock`) :
 * l'ouverture ne déclenche aucun appel réseau, donc aucune attente.
 */
export function KeyIndicatorsModal({
	open,
	onClose,
	title,
	indicators,
	details,
	viewAllLabel,
	viewAllHref,
}: {
	open: boolean;
	onClose: () => void;
	title: string;
	indicators: KeyIndicatorEntry[];
	details?: KeyIndicatorsDetails;
	viewAllLabel?: string;
	viewAllHref?: string;
}) {
	const tr = useT();
	const tuiles = construireTuiles(indicators, details);
	// Le fragment peut porter sa propre destination ; sinon le portail macro, qui existe
	// toujours. La case est donc TOUJOURS là, elle ne dépend d'aucune cuisson.
	const statsHref = viewAllHref || "/statistiques";
	const statsLabel = viewAllLabel || tr("allStatistics");

	return (
		<OverlayDialog
			open={open}
			onClose={onClose}
			labelledBy={TITLE_ID}
			// Mobile : feuille plein écran, chaque pixel compte. À partir de md : panneau
			// centré, borné en hauteur, dont le corps seul défile.
			className="h-[100dvh] max-w-none md:h-auto md:max-h-[90vh] md:w-[min(96vw,960px)] md:rounded-lg md:shadow-2xl"
		>
			<header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 md:px-7 md:py-5">
				<div className="min-w-0">
					<h2 id={TITLE_ID} className="text-[22px] font-bold leading-tight text-[#005CA9] md:text-[26px]">
						{title}
					</h2>
					<div className="mt-2 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
					<p className="mt-2 text-[13px] leading-5 text-slate-600 md:text-[14px]">{tr("keyIndicatorsModalLead")}</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label={tr("closeDialog")}
					className="-mr-2 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-[#EAF6FF] hover:text-[#005CA9]"
				>
					<X size={22} aria-hidden="true" />
				</button>
			</header>

			<div className="flex-1 overflow-y-auto bg-[#EDF5FB] px-5 py-5 md:px-7 md:py-6">
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
					{tuiles.map((tuile) => (
						<IndicatorTile key={`${tuile.label}-${tuile.value}`} tuile={tuile} />
					))}
					<StatisticsTile href={statsHref} label={statsLabel} onNavigate={onClose} />
				</div>
			</div>

		</OverlayDialog>
	);
}
